import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface LoginEntry {
  id: number;
  userEmail: string;
  ipAddress: string | null;
  forwardedIp: string | null;
  userAgent: string | null;
  authMethod: string | null;
  platform: string | null;
  timezone: string | null;
  screenResolution: string | null;
  connectionType: string | null;
  avgResponseMs: number | null;
  notificationsGranted: string | null;
  loggedInAt: string;
}

interface LogsResponse {
  logs: LoginEntry[];
  total: number;
  page: number;
  pageSize: number;
}

const AUTH_METHOD_COLORS: Record<string, string> = {
  password: "bg-slate-100 text-slate-700",
  entra: "bg-blue-100 text-blue-700",
  sso: "bg-purple-100 text-purple-700",
  saml: "bg-indigo-100 text-indigo-700",
};

function formatUserAgent(ua: string | null): string {
  if (!ua) return "—";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edge")) return "Edge";
  return ua.length > 40 ? ua.slice(0, 40) + "…" : ua;
}

export default function AgentLoginLogs() {
  const api = useApi();
  const [logs, setLogs] = useState<LoginEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [authMethodFilter, setAuthMethodFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const pageSize = 100;
  const totalPages = Math.ceil(total / pageSize);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });
      if (search) params.set("search", search);
      if (authMethodFilter) params.set("authMethod", authMethodFilter);
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const resp = await api.admin.get(`AgentLogin/site?${params}`);
      const data: LogsResponse = await resp.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setTotal(data.total ?? 0);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, page, search, authMethodFilter, fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function handleFilterChange() {
    setPage(1);
    fetchLogs();
  }

  return (
    <div className="p-6 max-w-7xl">
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          Security audit log of all agent logins — IP address, browser, device, and authentication method.
          Use this to detect unauthorized access or unusual login patterns.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Search email or IP…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button
            type="submit"
            className="bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              className="text-slate-500 text-sm underline"
              onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}
            >
              Clear
            </button>
          )}
        </form>

        <select
          className="border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={authMethodFilter}
          onChange={(e) => { setAuthMethodFilter(e.target.value); setPage(1); }}
        >
          <option value="">All auth methods</option>
          <option value="password">Password</option>
          <option value="entra">Entra / SSO</option>
          <option value="saml">SAML</option>
        </select>

        <input
          type="date"
          className="border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
          title="From date"
        />
        <span className="text-slate-400 text-sm self-center">to</span>
        <input
          type="date"
          className="border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(1); }}
          title="To date"
        />
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left font-medium">When</th>
              <th className="px-4 py-3 text-left font-medium">Agent</th>
              <th className="px-4 py-3 text-left font-medium">Auth Method</th>
              <th className="px-4 py-3 text-left font-medium">Browser</th>
              <th className="px-4 py-3 text-left font-medium">IP Address</th>
              <th className="px-4 py-3 text-left font-medium">Platform</th>
              <th className="px-4 py-3 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td>
              </tr>
            )}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  No login records found
                  {search || authMethodFilter || fromDate || toDate
                    ? " matching your filters"
                    : " — logins are recorded when agents sign in to the agent workspace"}
                  .
                </td>
              </tr>
            )}
            {!loading && logs.map((log) => (
              <React.Fragment key={log.id}>
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{log.loggedInAt}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{log.userEmail || "—"}</td>
                  <td className="px-4 py-3">
                    {log.authMethod ? (
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${AUTH_METHOD_COLORS[log.authMethod.toLowerCase()] ?? "bg-slate-100 text-slate-700"}`}>
                        {log.authMethod}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{formatUserAgent(log.userAgent)}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{log.ipAddress || log.forwardedIp || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{log.platform || "—"}</td>
                  <td className="px-4 py-3">
                    {(log.timezone || log.screenResolution || log.connectionType || log.avgResponseMs != null) && (
                      <button
                        onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                        className="text-blue-500 hover:text-blue-700 text-xs"
                        aria-label="Toggle details"
                      >
                        {expandedRow === log.id ? "▲" : "▼"}
                      </button>
                    )}
                  </td>
                </tr>
                {expandedRow === log.id && (
                  <tr className="bg-slate-50">
                    <td colSpan={7} className="px-4 pb-3 pt-0">
                      <div className="flex flex-wrap gap-4 text-xs text-slate-600 mt-1">
                        {log.timezone && <span><strong>Timezone:</strong> {log.timezone}</span>}
                        {log.screenResolution && <span><strong>Screen:</strong> {log.screenResolution}</span>}
                        {log.connectionType && <span><strong>Connection:</strong> {log.connectionType}</span>}
                        {log.avgResponseMs != null && <span><strong>Latency:</strong> {log.avgResponseMs}ms</span>}
                        {log.notificationsGranted && <span><strong>Notifications:</strong> {log.notificationsGranted}</span>}
                        {log.forwardedIp && log.forwardedIp !== log.ipAddress && (
                          <span><strong>Forwarded IP:</strong> {log.forwardedIp}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>Page {page} of {totalPages} &nbsp;({total.toLocaleString()} total)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        Login records are retained for 90 days. Each record includes IP address, browser fingerprint, and authentication method.
        Contact Velaro support to discuss extended retention or SIEM integration.
      </p>
    </div>
  );
}
